/**
 * «Добавить технику» — два шага: модель из каталога → поля по профилю
 * (AK-747, макет `docs/superpowers/prototypes/machinery-forms/01-add-machinery.html`,
 * утверждён founder 26.08).
 *
 * Обязательного — два поля: **модель и хозяйство**. Всё остальное можно
 * заполнить потом: машина уже считается в парке, и шлагбаум на заведении
 * означал бы, что парк просто не заведут.
 *
 * Модель — ТОЛЬКО из каталога. Из неё тянутся вид, производитель и профиль
 * ввода; марка, набранная руками («мтз 82» / «Беларус 82»), ни к одному
 * нормативу не привяжется, и парк превратится в три записи об одной машине.
 *
 * Профиль (трактор / комбайн / опрыскиватель / прицепная СХМ / транспорт /
 * стационарное) выбирает СЕРВЕР по виду модели — человек группу не выбирает.
 * Поля профиля и значения, которые каталог уже знает, приходят одним ответом
 * `GET /company/machinery/model-profile/{id}`: то же самое, чем сервер потом
 * проверяет запись, поэтому форма и валидация не разъезжаются.
 *
 * Чего в форме НЕТ намеренно: производительности га/ч, расхода л/га и
 * длительности смены. Это норматив на пару «операция × агрегат» (книга ЕТС уже
 * загружена) — спросив их здесь, мы завели бы второй, расходящийся ответ на
 * один вопрос.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stack } from '@/components/ui/stack';
import { useTextFilterDraft } from '@/lib/filters/useTextFilterDraft';
import { cn } from '@/lib/utils';
import { useMachineryCatalog, useModelProfile, type CatalogModel } from './queries';
import { MAX_CREATE_QUANTITY, parseQuantity, type ProfileField } from './machineryLogic';

export interface MachineryDraft {
  ref_id: string;
  reg_number: string | null;
  inventory_number: string | null;
  year_made: number | null;
  brigade_id: string | null;
  attrs: Record<string, string | number | boolean>;
  /** Поля профиля, у которых есть СВОЯ колонка (мощность, расход) — они уезжают
   *  отдельными полями запроса, а не внутри паспорта. */
  columns: Record<string, string>;
  /** Сколько одинаковых единиц завести разом (AK-764, Р22). */
  quantity: number;
}

/**
 * Подпись под списком каталога.
 *
 * Список алфавитный и урезан страницей (сервер отдаёт первые N по алфавиту) —
 * про урезание надо СКАЗАТЬ. Молчаливое «показали 50 из 1461» читается как
 * «в каталоге больше ничего нет», и человек идёт заводить дубль руками. Топ-N
 * по популярности здесь сознательно не делаем (канон выпадашек проекта):
 * порядок — алфавит, сужение — поиском, а не догадкой системы о том, что
 * человеку нужнее.
 */
export function catalogNotice(shown: number, total: number): string | null {
  if (total <= shown) return null;
  return `Показаны первые ${shown} из ${total} по алфавиту — уточните поиск.`;
}

/** Явные коды «ничего не выбрано»: Radix-Select пустую строку как значение
 *  пункта не принимает, а «пусто» на экране должно быть выбираемым. */
const NO_BRIGADE = '__none__';
const NOT_SET = '__unset__';

export interface BrigadeOption {
  id: string | null;
  name: string;
}

/** Год выпуска: пустое поле — это «не знаю», а не ноль и не ошибка. */
export function parseYear(raw: string): { value: number | null; error: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null, error: null };
  if (!/^\d{4}$/.test(trimmed)) {
    return { value: null, error: 'Год выпуска — четыре цифры, например 2019' };
  }
  const year = Number(trimmed);
  const limit = new Date().getFullYear() + 1;
  // Нижняя граница — ТА ЖЕ, что у сервера (`year_made: ge=1900`). Держать
  // форму строже бэка нельзя: она отбивала бы то, что API принимает, и
  // человек упирался бы в правило, которого в системе нет.
  if (year < 1900 || year > limit) {
    return { value: null, error: `Год выпуска между 1900 и ${limit}` };
  }
  return { value: year, error: null };
}

export function modelName(model: CatalogModel): string {
  return model.name_translations?.ru ?? model.name_translations?.en ?? model.code ?? '—';
}

/**
 * Черновик паспорта → то, что уедет на сервер.
 *
 * Пустое значение НЕ отправляем ключом с пустой строкой: пусто в паспорте
 * значит «не знаю», а `""` сервер обязан был бы либо отвергнуть, либо сохранить
 * как факт. Числа отдаём строкой — запятую в «8,0» разбирает сервер, тем же
 * кодом, что и импорт: два разных разбора одного формата разошлись бы.
 */
export function collectAttrs(
  fields: readonly ProfileField[],
  draft: Record<string, string | boolean>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const field of fields) {
    // Поле со своей колонкой в паспорт НЕ кладём: сервер такой ключ отвергает
    // (`machinery_attr_has_column`), потому что одно число в двух местах — это
    // два расходящихся ответа. Оно уезжает через `collectColumns`.
    if (field.column) continue;
    const value = draft[field.key];
    if (field.type === 'bool') {
      if (value === true) out[field.key] = true;
      continue;
    }
    if (typeof value === 'string' && value.trim()) out[field.key] = value.trim();
  }
  return out;
}

/** Поля профиля, у которых есть своя колонка → корень запроса. */
export function collectColumns(
  fields: readonly ProfileField[],
  draft: Record<string, string | boolean>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of fields) {
    if (!field.column) continue;
    const value = draft[field.key];
    if (typeof value === 'string' && value.trim()) out[field.key] = value.trim();
  }
  return out;
}

export function AddMachineryDialog({
  open,
  farmName,
  brigades,
  pending,
  onCancel,
  onSave,
}: {
  open: boolean;
  farmName: string;
  brigades: readonly BrigadeOption[];
  pending: boolean;
  onCancel: () => void;
  /** `keepOpen` — «Сохранить и добавить ещё»: форма вернётся к шагу выбора
   *  модели, а хозяйство и отделение останутся заполненными. */
  onSave: (draft: MachineryDraft, keepOpen: boolean) => Promise<boolean>;
}) {
  const [step, setStep] = useState<'model' | 'details'>('model');
  const [search, setSearch] = useState('');
  // Поиск с паузой — общий хук, свой дребезг не изобретаем: без неё каждая
  // буква уходила запросом в каталог на 657 марок.
  const [searchDraft, setSearchDraft] = useTextFilterDraft(search, setSearch);
  const [picked, setPicked] = useState<CatalogModel | null>(null);
  const [reg, setReg] = useState('');
  const [inv, setInv] = useState('');
  const [year, setYear] = useState('');
  const [brigadeId, setBrigadeId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [passport, setPassport] = useState<Record<string, string | boolean>>({});
  const [passportOpen, setPassportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetAll = () => {
    setStep('model');
    setSearch('');
    setSearchDraft('');
    setPicked(null);
    setReg('');
    setInv('');
    setYear('');
    setQuantity('1');
    setPassport({});
    setPassportOpen(false);
    setError(null);
  };

  useEffect(() => {
    if (!open) return;
    resetAll();
    setBrigadeId(null);
  }, [open]);

  // Сменил модель — паспорт чистим ПОЛНОСТЬЮ. Иначе мощность 428 л.с.,
  // подставленная каталогом для К-744, молча уезжает с МТЗ-82: эффект
  // дозаполнения занятые ключи пропускает, а секция паспорта свёрнута, и
  // человек этого не видит (блокер ревью head-web по #932). Номера, год и
  // отделение НЕ трогаем — они про физическую машину, а не про модель.
  const pickedId = picked?.id ?? null;
  useEffect(() => {
    setPassport({});
  }, [pickedId]);

  const catalog = useMachineryCatalog(search, open && step === 'model');
  const models = useMemo(() => catalog.data?.items ?? [], [catalog.data]);
  const catalogEmpty = !catalog.isLoading && catalog.data?.total === 0;

  const profileQuery = useModelProfile(picked?.id ?? null, open && step === 'details');
  const profile = profileQuery.data;
  const profileFields = useMemo(() => profile?.fields ?? [], [profile]);

  // Значения, которые каталог уже знает, показываем заполненными — переспрашивать
  // то, что система знает, значит заставлять человека сверяться с ней вручную.
  useEffect(() => {
    if (!profile) return;
    setPassport((prev) => {
      const next = { ...prev };
      for (const field of profile.fields) {
        if (next[field.key] !== undefined) continue;
        if (field.value === null || field.value === undefined) continue;
        next[field.key] = field.type === 'bool' ? Boolean(field.value) : String(field.value);
      }
      return next;
    });
  }, [profile]);

  const submit = async (keepOpen: boolean) => {
    if (!picked) {
      setError('Выберите модель из каталога — по ней подтянутся вид, профиль и нормативы.');
      return;
    }
    const parsedYear = parseYear(year);
    if (parsedYear.error) {
      setError(parsedYear.error);
      return;
    }
    const parsedQty = parseQuantity(quantity);
    if (parsedQty.error) {
      setError(parsedQty.error);
      return;
    }
    // Номер принадлежит ОДНОЙ машине. Проверяем это здесь, а не только на
    // сервере, потому что человеку нужно не «422», а понимание, что делать:
    // либо заводить пачку без номеров, либо по одной с номерами.
    if (parsedQty.value > 1 && (reg.trim() || inv.trim())) {
      setError(
        'Гос. и инвентарный номер принадлежат одной машине — заведите пачку без номеров и проставьте их каждой отдельно.',
      );
      return;
    }
    setError(null);
    const ok = await onSave(
      {
        ref_id: picked.id,
        quantity: parsedQty.value,
        // Пусто ≠ мусор: пустая строка уедет как NULL, иначе две машины
        // «без номера» столкнулись бы на уникальности гос.номера.
        reg_number: reg.trim() || null,
        inventory_number: inv.trim() || null,
        year_made: parsedYear.value,
        brigade_id: brigadeId,
        attrs: collectAttrs(profileFields, passport),
        columns: collectColumns(profileFields, passport),
      },
      keepOpen,
    );
    if (ok && keepOpen) {
      // Хозяйство и отделение НЕ сбрасываем: «добавить ещё» — это обычно
      // следующая машина того же отделения, и переспрашивать их каждый раз
      // значит красть у человека два клика на каждой строке парка.
      resetAll();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="truncate">
            Добавить технику{picked && step === 'details' ? ` · ${modelName(picked)}` : ''}
          </DialogTitle>
          <DialogDescription>
            {step === 'model'
              ? 'Шаг 1 из 2 — модель из каталога.'
              : `Шаг 2 из 2 — номера и паспорт. Машина встанет в парк хозяйства «${farmName}».`}
          </DialogDescription>
        </DialogHeader>

        {step === 'model' ? (
          <Stack gap={3}>
            <div>
              <Label htmlFor="machinery-model">Модель из каталога *</Label>
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="machinery-model"
                  className="pl-8"
                  value={searchDraft}
                  placeholder="Поиск по названию, коду МДМ и былым наименованиям"
                  aria-invalid={error && !picked ? true : undefined}
                  onChange={(e) => setSearchDraft(e.target.value)}
                />
              </div>
            </div>

            <ScrollArea className="h-48 rounded-md border border-border">
              {catalog.isLoading ? (
                <p className="p-3 text-sm text-muted-foreground">Гружу каталог…</p>
              ) : catalogEmpty ? (
                // Пустой каталог — это НЕ «ничего не найдено»: справочник может
                // быть просто не залит, и человеку важна разница.
                <p className="p-3 text-sm text-muted-foreground" data-testid="machinery-catalog-empty">
                  {search.trim()
                    ? 'По запросу ничего не нашлось. Нет модели в каталоге — напишите нам, добавим в справочник: свободный ввод марки запрещён, иначе нормы расхода и выработки не привяжутся.'
                    : 'Каталог моделей пока пуст — техника заводится после его загрузки.'}
                </p>
              ) : (
                <ul data-testid="machinery-catalog-list">
                  {models.map((model) => (
                    <li key={model.id}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                          picked?.id === model.id && 'bg-muted font-semibold',
                        )}
                        onClick={() => {
                          setPicked(model);
                          setError(null);
                        }}
                      >
                        <span className="flex-1">{modelName(model)}</span>
                        {model.manufacturer_name && (
                          <span className="text-xs text-muted-foreground">
                            {model.manufacturer_name}
                          </span>
                        )}
                        {model.type_name && (
                          <span className="text-xs text-muted-foreground">{model.type_name}</span>
                        )}
                        {picked?.id === model.id && <Check className="h-4 w-4" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>

            {catalogNotice(models.length, catalog.data?.total ?? 0) && (
              <p className="text-xs text-muted-foreground" data-testid="machinery-catalog-notice">
                {catalogNotice(models.length, catalog.data?.total ?? 0)}
              </p>
            )}

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </Stack>
        ) : (
          <Stack gap={4}>
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
              <span className="font-semibold">{modelName(picked as CatalogModel)}</span>
              {profile?.manufacturer_name && (
                <span className="text-muted-foreground">{profile.manufacturer_name}</span>
              )}
              {profile?.type_name && <Badge variant="secondary">{profile.type_name}</Badge>}
              {profile?.profile_label && (
                <span className="text-xs text-muted-foreground">{profile.profile_label}</span>
              )}
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('model')}
                data-testid="machinery-change-model"
              >
                Изменить модель
              </Button>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="min-w-[160px] flex-1">
                <Label htmlFor="machinery-reg">Гос. номер</Label>
                <Input
                  id="machinery-reg"
                  className="mt-1"
                  value={reg}
                  placeholder="12 АА 3456"
                  onChange={(e) => setReg(e.target.value)}
                />
              </div>
              <div className="min-w-[160px] flex-1">
                <Label htmlFor="machinery-inv">Инвентарный №</Label>
                <Input
                  id="machinery-inv"
                  className="mt-1"
                  value={inv}
                  placeholder="000481"
                  onChange={(e) => setInv(e.target.value)}
                />
              </div>
              <div className="w-32">
                <Label htmlFor="machinery-year">Год выпуска</Label>
                <Input
                  id="machinery-year"
                  className="mt-1"
                  inputMode="numeric"
                  value={year}
                  placeholder="2019"
                  onChange={(e) => setYear(e.target.value)}
                />
              </div>
              <div className="w-32">
                <Label htmlFor="machinery-quantity">Количество</Label>
                <Input
                  id="machinery-quantity"
                  className="mt-1"
                  inputMode="numeric"
                  value={quantity}
                  placeholder="1"
                  data-testid="machinery-quantity"
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
            </div>

            {parseQuantity(quantity).value > 1 && (
              // Говорим ДО сохранения, а не после отказа: человек, заводящий
              // десять сеялок, номера в форме уже видит и логично пытается их
              // заполнить.
              <p className="text-xs text-muted-foreground" data-testid="machinery-quantity-hint">
                Заведём {parseQuantity(quantity).value} одинаковых единиц (максимум{' '}
                {MAX_CREATE_QUANTITY} за раз). Гос. и инвентарный номер у них должны
                остаться пустыми — номер принадлежит одной машине, проставьте его
                каждой отдельно.
              </p>
            )}

            <div>
              <Label htmlFor="machinery-brigade">Отделение</Label>
              <Select
                value={brigadeId ?? NO_BRIGADE}
                onValueChange={(v) => setBrigadeId(v === NO_BRIGADE ? null : v)}
              >
                <SelectTrigger id="machinery-brigade" className="mt-1" data-testid="machinery-brigade">
                  <SelectValue placeholder="Не выбрано" />
                </SelectTrigger>
                <SelectContent>
                  {/* Radix не принимает пустую строку как значение пункта —
                      «не выбрано» несёт свой явный код. */}
                  <SelectItem value={NO_BRIGADE}>Не выбрано</SelectItem>
                  {brigades
                    .filter((b) => b.id !== null)
                    .map((b) => (
                      <SelectItem key={b.id as string} value={b.id as string}>
                        {b.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                Только отделения хозяйства «{farmName}». При перегоне в другое
                хозяйство отделение снимается.
              </p>
            </div>

            <div className="rounded-md border border-border">
              <button
                type="button"
                className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium"
                onClick={() => setPassportOpen((v) => !v)}
                data-testid="machinery-passport-toggle"
              >
                {passportOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                Уточнить паспорт{profile ? ` · ${profile.profile_label.toLowerCase()}` : ''}
                <span className="text-xs font-normal text-muted-foreground">необязательно</span>
              </button>

              {passportOpen && (
                <div className="border-t border-border p-3">
                  {profileQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Гружу профиль модели…</p>
                  ) : (
                    <Stack gap={3}>
                      {profileFields.map((field) => (
                        <PassportField
                          key={field.key}
                          field={field}
                          value={passport[field.key]}
                          onChange={(v) => setPassport((prev) => ({ ...prev, [field.key]: v }))}
                        />
                      ))}
                    </Stack>
                  )}
                </div>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </Stack>
        )}

        <DialogFooter>
          {step === 'details' && (
            <Button variant="ghost" onClick={() => setStep('model')} disabled={pending}>
              ← Назад
            </Button>
          )}
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          {step === 'model' ? (
            <Button
              onClick={() => {
                if (!picked) {
                  setError('Выберите модель из каталога — по ней подтянутся вид, профиль и нормативы.');
                  return;
                }
                setError(null);
                setStep('details');
              }}
              data-testid="machinery-next"
            >
              Далее →
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={pending}
                onClick={() => void submit(true)}
                data-testid="machinery-add-more"
              >
                Сохранить и добавить ещё
              </Button>
              <Button
                disabled={pending}
                onClick={() => void submit(false)}
                data-testid="machinery-add-confirm"
              >
                {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Сохранить
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PassportField({
  field,
  value,
  onChange,
}: {
  field: ProfileField;
  value: string | boolean | undefined;
  onChange: (value: string | boolean) => void;
}) {
  const id = `passport-${field.key}`;
  if (field.type === 'bool') {
    return (
      <label className="flex items-center gap-2 text-sm" htmlFor={id}>
        <Checkbox
          id={id}
          checked={value === true}
          onCheckedChange={(v) => onChange(v === true)}
        />
        {field.label}
      </label>
    );
  }
  return (
    <div>
      <Label htmlFor={id}>
        {field.label}
        {field.from_catalog && (
          <span className="ml-2 text-xs font-normal text-muted-foreground">из каталога</span>
        )}
      </Label>
      {field.type === 'enum' ? (
        <Select
          value={typeof value === 'string' && value ? value : NOT_SET}
          onValueChange={(v) => onChange(v === NOT_SET ? '' : v)}
        >
          <SelectTrigger id={id} className="mt-1">
            <SelectValue placeholder="Не выбрано" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NOT_SET}>Не выбрано</SelectItem>
            {(field.options ?? []).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          className="mt-1"
          inputMode={field.type === 'number' ? 'decimal' : undefined}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {field.help && <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>}
    </div>
  );
}
